// attendanceThunks.js - UPDATED for simplified backend structure
import { createAsyncThunk } from '@reduxjs/toolkit';
import attendanceApi from '../../api/attendanceApi';

// ==================== TODAY'S SUMMARY ====================
export const fetchTodaySummary = createAsyncThunk(
  'attendance/fetchTodaySummary',
  async (_, { rejectWithValue }) => {
    try {
      const response = await attendanceApi.getTodaySummary();
      const { summary, records } = response.data;
      return {
        total: summary.total_records || 0,
        present: summary.present || 0,
        absent: summary.absent || 0,
        late: summary.late || 0,
        halfDay: summary.half_day || 0,
        onLeave: summary.on_leave || 0,
        medicalLeave: summary.medical_leave || 0,
        workFromHome: summary.work_from_home || 0,
        totalWorkHours: summary.total_work_hours || 0,
        avgWorkHours: summary.avg_work_hours || 0,
        totalEmployees: summary.total_employees || 0,
        employeesWithRecords: summary.employees_with_records || 0,
        employeesWithoutRecords: summary.employees_without_records || 0,
        date: summary.date,
        records: records || [],
      };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch today\'s summary');
    }
  }
);

// ==================== MY ATTENDANCE ====================
export const fetchMyAttendance = createAsyncThunk(
  'attendance/fetchMyAttendance',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await attendanceApi.getMyAttendance(params);
      const { employee, summary, records, count } = response.data;
      return {
        employeeId: employee.id,
        employeeName: employee.name,
        employeeCode: employee.employee_code,
        department: employee.department,
        role: employee.role,
        records: records || [],
        summary: {
          present: summary.present || 0,
          absent: summary.absent || 0,
          late: summary.late || 0,
          halfDay: summary.half_day || 0,
          onLeave: summary.on_leave || 0,
          medicalLeave: summary.medical_leave || 0,
          totalRecords: summary.total_records || 0,
          totalWorkHours: summary.total_work_hours || 0,
          avgWorkHours: summary.avg_work_hours || 0,
        },
        count: count || 0,
      };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch your attendance');
    }
  }
);

// ==================== EMPLOYEE SUMMARY ====================
export const fetchEmployeeSummary = createAsyncThunk(
  'attendance/fetchEmployeeSummary',
  async ({ employeeId, ...params }, { rejectWithValue }) => {
    try {
      if (!employeeId) return rejectWithValue('Employee ID is required');
      const response = await attendanceApi.getEmployeeSummary(employeeId, params);
      const { employee, summary, records, count, total_employees } = response.data;
      return {
        employeeId: employee.id,
        employeeName: employee.name,
        employeeCode: employee.employee_code,
        department: employee.department,
        role: employee.role,
        records: records || [],
        summary: {
          present: summary.present || 0,
          absent: summary.absent || 0,
          late: summary.late || 0,
          halfDay: summary.half_day || 0,
          onLeave: summary.on_leave || 0,
          medicalLeave: summary.medical_leave || 0,
          totalRecords: summary.total_records || 0,
          totalWorkHours: summary.total_work_hours || 0,
          avgWorkHours: summary.avg_work_hours || 0,
        },
        count: count || 0,
        totalEmployees: total_employees || 0,
      };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch employee summary');
    }
  }
);

// ==================== LIVE ATTENDANCE ====================
export const fetchLiveAttendance = createAsyncThunk(
  'attendance/fetchLiveAttendance',
  async (_, { rejectWithValue }) => {
    try {
      const response = await attendanceApi.getLiveAttendance();
      return response.data.records || [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch live attendance');
    }
  }
);

// ==================== LEGACY ====================
/** @deprecated Use fetchEmployeeSummary instead */
export const fetchEmployeeAttendance = fetchEmployeeSummary;
/** @deprecated Use fetchTodaySummary instead */
export const fetchAttendanceRecords = fetchTodaySummary;

// ==================== UPDATE STATUS ====================
/**
 * Update attendance status
 * PATCH /api/attendance/{id}/update-status/
 * Permissions: Admin, HR, Department Head
 */
export const updateAttendanceStatus = createAsyncThunk(
  'attendance/updateStatus',
  async ({ recordId, status, remarks = '', oldStatus }, { rejectWithValue }) => {
    try {
      const response = await attendanceApi.updateAttendanceStatus(recordId, { status, remarks });
      // Return the nested attendance object if present
      return response.data?.attendance || response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to update attendance status');
    }
  }
);

// ==================== UPDATE PUNCHES ====================
/**
 * Update punch in / punch out times (and optionally remarks)
 * PATCH /api/attendance/{id}/update-punches/
 * Permissions: Admin, HR only
 *
 * @param {object} payload
 * @param {number} payload.recordId   - Attendance record ID
 * @param {string} [payload.punch_in]  - ISO-8601 datetime string, e.g. "2025-02-20T09:05:00"
 * @param {string} [payload.punch_out] - ISO-8601 datetime string
 * @param {string} [payload.remarks]   - Optional free-text reason
 */
export const updateAttendancePunches = createAsyncThunk(
  'attendance/updatePunches',
  async ({ recordId, punch_in, punch_out, remarks = '' }, { rejectWithValue }) => {
    try {
      if (!punch_in && !punch_out) {
        return rejectWithValue('Provide at least one of: punch_in or punch_out');
      }
      const payload = { remarks };
      if (punch_in)  payload.punch_in  = punch_in;
      if (punch_out) payload.punch_out = punch_out;

      const response = await attendanceApi.updatePunches(recordId, payload);
      // Backend returns { success, message, attendance, ... }
      return response.data?.attendance || response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error ||
        error.response?.data?.message ||
        'Failed to update punch times'
      );
    }
  }
);

// ==================== CREATE ATTENDANCE ====================
export const createAttendance = createAsyncThunk(
  'attendance/create',
  async (attendanceData, { rejectWithValue }) => {
    try {
      const response = await attendanceApi.createAttendance(attendanceData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to create attendance');
    }
  }
);

// ==================== DELETE ATTENDANCE ====================
export const deleteAttendance = createAsyncThunk(
  'attendance/delete',
  async (attendanceId, { rejectWithValue }) => {
    try {
      await attendanceApi.deleteAttendance(attendanceId);
      return attendanceId;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to delete attendance');
    }
  }
);

// ==================== BULK UPDATE ====================
export const bulkUpdateAttendance = createAsyncThunk(
  'attendance/bulkUpdate',
  async (records, { rejectWithValue }) => {
    try {
      const response = await attendanceApi.bulkUpdateAttendance(records);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to bulk update attendance');
    }
  }
);

// ==================== EXPORT ====================
export const exportAttendance = createAsyncThunk(
  'attendance/export',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await attendanceApi.exportAttendanceCSV(params);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `attendance_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      return { success: true };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to export attendance');
    }
  }
);